import { useState } from 'react';

import {
  Button,
  Circle,
  HStack,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  type ModalProps,
  Switch,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import { Trans, useTranslation } from 'react-i18next';

import { IconCloudUploadBrand } from '@/components/Icons';
import { Link } from '@/components/Link';
import { UploadDropZone } from '@/components/UploadDropZone';
import { useUploadFileMutationLegacy } from '@/features/file';
import { type MailTemplateDto } from '@/services/api';

import { EmailTemplateSelect } from '../../components/EmailTemplateSelect';
import { useIsTemplateInterview } from '../admin-interview';
import { useAddTemplateToInterviewMutation } from '../admin-interview/useAdminInterviewQueries';
import { DuplicatedEmailsModal } from './DuplicatedEmailsModal';
import { useAnswerExcelImportMutation } from './useCandidateTableMutation';

type ImportExcelModalProps = Omit<ModalProps, 'children'> & {
  interviewId: string;
};

export function ImportExcelModal({
  interviewId,
  ...props
}: ImportExcelModalProps) {
  const { t } = useTranslation(['candidate']);
  const [selectedTemplateId, setSelectedTemplateId] =
    useState<MailTemplateDto['id']>();

  const isTemplate = useIsTemplateInterview();
  const fileUploadMutation = useUploadFileMutationLegacy();
  const [file, setFile] = useState<File | null>(null);
  const [sendInvitation, setSendInvitation] = useState<boolean>(true);

  const [duplicatedEmails, setDuplicatedEmails] = useState<Array<string>>([]);
  const {
    isOpen: isDuplicatedEmailsModalOpen,
    onOpen: onDuplicatedEmailsModalOpen,
    onClose: onDuplicatedEmailsModalClose,
  } = useDisclosure();

  const importExcelMutation = useAnswerExcelImportMutation({
    onDuplicatedEmails: (emails) => {
      setDuplicatedEmails(emails);
      onDuplicatedEmailsModalOpen();
    },
  });

  const addTemplateToInterview = useAddTemplateToInterviewMutation();
  const createInterviewFromTemplate = async () => {
    await addTemplateToInterview.mutateAsync(interviewId);
    const url = new URL(window.location.href);
    url.searchParams.delete('isTemplate');
    window.history.replaceState({}, '', url.toString());
  };

  const submitFile = async (file: Blob, interviewId: string) => {
    if (!interviewId) {
      return;
    }

    if (isTemplate) await createInterviewFromTemplate();

    const uploadRes = await fileUploadMutation.mutateAsync({
      access: {
        interviewId: interviewId,
        candidateId: 'candidateId',
        questionId: 'question?.id',
        contentType: file.type,
      },
      file,
    });

    await importExcelMutation.mutateAsync({
      interviewId: interviewId,
      body: {
        fileUrl: uploadRes.finalObjectUrl,
        mailTemplateId: selectedTemplateId,
        sendInvitation: sendInvitation,
      },
    });

    props.onClose();
    setFile(null);
  };

  const handleImportWitExcel = () => {
    submitFile(file!, interviewId);
  };

  return (
    <>
      <Modal {...props} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <ModalCloseButton />
          </ModalHeader>

          <ModalBody>
            <VStack gap={2}>
              <Heading lineHeight={1} fontSize="xl" textAlign="center">
                {t('candidate:importByExcel.title')}
              </Heading>
              <Text mb={4} fontSize="sm" textAlign="center">
                {t('candidate:importByExcel.description')}
              </Text>

              <UploadDropZone
                w="full"
                cursor="pointer"
                onDrop={([file]) => {
                  if (file) setFile(file);
                }}
                dropZoneOptions={{
                  accept: {
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                      ['.xlsx'],
                  },
                  multiple: false,
                }}
              >
                <Circle size="40px" bg="brand.50" fontSize={20}>
                  <IconCloudUploadBrand />
                </Circle>
                {file ? (
                  <Text fontSize="sm">{file.name}</Text>
                ) : (
                  <Trans
                    t={t}
                    parent={(props) => <Text textAlign="center" {...props} />}
                    i18nKey="candidate:importByExcel.fileInstruction"
                    components={[
                      <Text color="brand.700" as="span" key="0" />,
                      <Text as="span" key="1" />,
                      <Text fontSize="sm" key="2" />,
                    ]}
                  />
                )}
              </UploadDropZone>
              <Trans
                t={t}
                parent={(props) => (
                  <Text mt={3} fontSize="xs" textAlign="center" {...props} />
                )}
                i18nKey="candidate:importByExcel.exampleFile"
                components={{
                  Link: (
                    <Link
                      color="brand.700"
                      to="https://cdn.synapsai.ai/excel/synapsai_candidates_example.xlsx"
                    />
                  ),
                }}
              />

              <VStack gap={4} w="100%">
                <EmailTemplateSelect
                  selectedTemplateId={selectedTemplateId}
                  setSelectedTemplateId={setSelectedTemplateId}
                  isDisabled={!sendInvitation}
                />
                <HStack mr="auto">
                  <Switch
                    isChecked={sendInvitation}
                    onChange={(e) => setSendInvitation(e.target.checked)}
                    colorScheme="brand"
                    size="lg"
                  />
                  <Text fontWeight="medium" fontSize="md">
                    {t('candidate:inviteNewCandidate.sendInvitation')}
                  </Text>
                </HStack>
              </VStack>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button
              isDisabled={!file}
              variant="@primary"
              w="full"
              onClick={handleImportWitExcel}
              isLoading={
                importExcelMutation.isPending || fileUploadMutation.isPending
              }
            >
              {t('candidate:importByExcel.upload')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <DuplicatedEmailsModal
        isOpen={isDuplicatedEmailsModalOpen}
        onClose={onDuplicatedEmailsModalClose}
        duplicatedEmails={duplicatedEmails}
      />
    </>
  );
}
